import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronLeft,
    FiChevronRight,
    FiFileText,
    FiRefreshCw,
    FiSearch,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchPurchaseGstRegister,

    purchaseGstRecordsSelector,
    purchaseGstSummarySelector,
    purchaseGstPaginationSelector,

    gstReportLoadingSelector,
    gstReportErrorSelector,
} from "../../../../../../../store/billing/gst/gstReportSlice";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",

            currency: "INR",

            minimumFractionDigits: 2,

            maximumFractionDigits: 2,
        }
    ).format(
        toNumber(
            value
        )
    );


const formatDate = (
    value
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",

            month: "short",

            year: "numeric",
        }
    ).format(
        date
    );
};


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PurchaseGstRegister = ({
    fromDate,
    toDate,
    financialYear,
}) => {
    const dispatch =
        useDispatch();


    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const records =
        useSelector(
            purchaseGstRecordsSelector
        ) || [];

    const summary =
        useSelector(
            purchaseGstSummarySelector
        ) || {};

    const pagination =
        useSelector(
            purchaseGstPaginationSelector
        ) || {};

    const loading =
        useSelector(
            gstReportLoadingSelector
        ) || {};

    const errors =
        useSelector(
            gstReportErrorSelector
        ) || {};


    /*
    |--------------------------------------------------------------------------
    | FILTERS
    |--------------------------------------------------------------------------
    */

    const [
        searchInput,
        setSearchInput,
    ] = useState("");

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        status,
        setStatus,
    ] = useState(
        "CONFIRMED"
    );

    const [
        taxType,
        setTaxType,
    ] = useState(
        "ALL"
    );

    const [
        gstin,
        setGstin,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);


    /*
    |--------------------------------------------------------------------------
    | LOAD DATA
    |--------------------------------------------------------------------------
    */

    const loadData =
        useCallback(
            async () => {
                await dispatch(
                    fetchPurchaseGstRegister({
                        page,

                        limit,

                        fromDate,

                        toDate,

                        financialYear,

                        status,

                        taxType,

                        gstin,

                        search,
                    })
                );
            },
            [
                dispatch,

                page,
                limit,

                fromDate,
                toDate,
                financialYear,

                status,
                taxType,
                gstin,
                search,
            ]
        );


    useEffect(
        () => {
            loadData();
        },
        [
            loadData,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | RESET PAGE WHEN PERIOD CHANGES
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            setPage(1);
        },
        [
            fromDate,
            toDate,
            financialYear,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | SEARCH DEBOUNCE
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        setSearch(
                            searchInput.trim()
                        );

                        setPage(1);
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            searchInput,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const total =
        toNumber(
            pagination?.total
        );

    const currentPage =
        toNumber(
            pagination?.page,
            page
        ) || 1;

    const currentLimit =
        toNumber(
            pagination?.limit,
            limit
        ) || 20;

    const totalPages =
        Math.max(
            toNumber(
                pagination?.totalPages,
                1
            ),
            1
        );

    const fromRecord =
        total > 0
            ? (
                currentPage -
                1
            ) *
                currentLimit +
                1
            : 0;

    const toRecord =
        total > 0
            ? Math.min(
                currentPage *
                    currentLimit,
                total
            )
            : 0;


    /*
    |--------------------------------------------------------------------------
    | RESET FILTERS
    |--------------------------------------------------------------------------
    */

    const handleResetFilters =
        () => {
            setSearchInput(
                ""
            );

            setSearch(
                ""
            );

            setStatus(
                "CONFIRMED"
            );

            setTaxType(
                "ALL"
            );

            setGstin(
                ""
            );

            setPage(
                1
            );
        };


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="gst-purchase-register">

            {/* =====================================================
                ERROR
            ===================================================== */}

            {errors.purchaseRegister && (

                <div className="gst-dashboard-alert">

                    <FiAlertCircle />

                    <span>
                        {errors.purchaseRegister}
                    </span>

                </div>

            )}


            {/* =====================================================
                SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-summary-grid">

                <div className="gst-dashboard-summary-card">

                    <span>
                        Documents
                    </span>

                    <strong>
                        {toNumber(
                            summary?.totalDocuments
                        )}
                    </strong>

                    <small>
                        Purchase GST documents
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Taxable Purchase
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.taxableAmount
                        )}
                    </strong>

                    <small>
                        Taxable purchase value
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Input GST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.inputTax
                        )}
                    </strong>

                    <small>
                        Total purchase input tax
                    </small>

                </div>


                <div className="gst-dashboard-summary-card">

                    <span>
                        Purchase Total
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.grandTotal
                        )}
                    </strong>

                    <small>
                        Total purchase value
                    </small>

                </div>

            </section>


            {/* =====================================================
                TAX SUMMARY
            ===================================================== */}

            <section className="gst-dashboard-stat-grid gst-purchase-tax-summary">

                <div>

                    <span>
                        CGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.cgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        SGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.sgstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        IGST
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.igstAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        Cess
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.cessAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        RCM Taxable
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.reverseChargeTaxableAmount
                        )}
                    </strong>

                </div>


                <div>

                    <span>
                        RCM Tax
                    </span>

                    <strong>
                        {formatCurrency(
                            summary?.reverseChargeTaxAmount
                        )}
                    </strong>

                </div>

            </section>


            {/* =====================================================
                FILTERS
            ===================================================== */}

            <section className="gst-purchase-toolbar">

                <div className="gst-purchase-search">

                    <FiSearch />

                    <input
                        type="text"
                        value={
                            searchInput
                        }
                        placeholder="Search bill, invoice, supplier, GSTIN..."
                        onChange={(
                            event
                        ) =>
                            setSearchInput(
                                event.target.value
                            )
                        }
                    />

                </div>


                <input
                    type="text"
                    value={
                        gstin
                    }
                    placeholder="Supplier GSTIN"
                    onChange={(
                        event
                    ) => {
                        setGstin(
                            event.target.value
                        );

                        setPage(1);
                    }}
                />


                <select
                    value={
                        status
                    }
                    onChange={(
                        event
                    ) => {
                        setStatus(
                            event.target.value
                        );

                        setPage(1);
                    }}
                >

                    <option value="CONFIRMED">
                        Confirmed
                    </option>

                    <option value="ALL">
                        All Status
                    </option>

                    <option value="DRAFT">
                        Draft
                    </option>

                    <option value="CANCELLED">
                        Cancelled
                    </option>

                </select>


                <select
                    value={
                        taxType
                    }
                    onChange={(
                        event
                    ) => {
                        setTaxType(
                            event.target.value
                        );

                        setPage(1);
                    }}
                >

                    <option value="ALL">
                        All Tax Types
                    </option>

                    <option value="CGST_SGST">
                        CGST + SGST
                    </option>

                    <option value="IGST">
                        IGST
                    </option>

                </select>


                <button
                    type="button"
                    className="gst-dashboard-button"
                    disabled={
                        loading.purchaseRegister
                    }
                    onClick={
                        loadData
                    }
                >

                    <FiRefreshCw
                        className={
                            loading.purchaseRegister
                                ? "gst-dashboard-spinner"
                                : ""
                        }
                    />

                    Refresh

                </button>


                <button
                    type="button"
                    className="gst-dashboard-button"
                    onClick={
                        handleResetFilters
                    }
                >
                    Reset Filters
                </button>

            </section>


            {/* =====================================================
                TABLE
            ===================================================== */}

            <section className="gst-purchase-table-card">

                {loading.purchaseRegister &&
                records.length === 0 ? (

                    <div className="gst-purchase-empty">

                        <FiRefreshCw className="gst-dashboard-spinner" />

                        <strong>
                            Loading Purchase GST Register...
                        </strong>

                    </div>

                ) : records.length === 0 ? (

                    <div className="gst-purchase-empty">

                        <FiFileText />

                        <strong>
                            No Purchase GST records found
                        </strong>

                        <span>
                            Try changing the GST period or filters.
                        </span>

                    </div>

                ) : (

                    <div className="gst-purchase-table-wrapper">

                        <table className="gst-purchase-table">

                            <thead>

                                <tr>

                                    <th>
                                        Purchase Bill
                                    </th>

                                    <th>
                                        Bill Date
                                    </th>

                                    <th>
                                        Supplier
                                    </th>

                                    <th>
                                        GSTIN
                                    </th>

                                    <th>
                                        Supplier Invoice
                                    </th>

                                    <th>
                                        Place of Supply
                                    </th>

                                    <th>
                                        Tax Type
                                    </th>

                                    <th className="text-right">
                                        Taxable
                                    </th>

                                    <th className="text-right">
                                        CGST
                                    </th>

                                    <th className="text-right">
                                        SGST
                                    </th>

                                    <th className="text-right">
                                        IGST
                                    </th>

                                    <th className="text-right">
                                        Cess
                                    </th>

                                    <th className="text-right">
                                        Input Tax
                                    </th>

                                    <th className="text-right">
                                        Total
                                    </th>

                                    <th>
                                        RCM
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {records.map(
                                    (
                                        item,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                item?._id ||
                                                index
                                            }
                                        >

                                            {/* BILL */}

                                            <td>

                                                <strong className="gst-purchase-document-number">

                                                    {item?.documentNumber ||
                                                        "-"}

                                                </strong>

                                            </td>


                                            {/* BILL DATE */}

                                            <td>

                                                {formatDate(
                                                    item?.documentDate
                                                )}

                                            </td>


                                            {/* SUPPLIER */}

                                            <td>

                                                <div className="gst-purchase-supplier">

                                                    <strong>

                                                        {item?.supplier
                                                            ?.supplierName ||
                                                            "-"}

                                                    </strong>

                                                </div>

                                            </td>


                                            {/* GSTIN */}

                                            <td>

                                                <span className="gst-purchase-gstin">

                                                    {item?.supplier?.gstin ||
                                                        "-"}

                                                </span>

                                            </td>


                                            {/* INVOICE */}

                                            <td>

                                                <div className="gst-purchase-supplier">

                                                    <strong>

                                                        {item?.supplierInvoiceNumber ||
                                                            "-"}

                                                    </strong>

                                                    <span>

                                                        {formatDate(
                                                            item?.supplierInvoiceDate
                                                        )}

                                                    </span>

                                                </div>

                                            </td>


                                            {/* PLACE OF SUPPLY */}

                                            <td>

                                                {item?.placeOfSupply ||
                                                    item?.placeOfSupplyStateCode ||
                                                    "-"}

                                            </td>


                                            {/* TAX TYPE */}

                                            <td>

                                                <span className="gst-purchase-tax-type">

                                                    {String(
                                                        item?.taxType ||
                                                        "-"
                                                    ).replace(
                                                        /_/g,
                                                        " "
                                                    )}

                                                </span>

                                            </td>


                                            {/* TAXABLE */}

                                            <td className="text-right">

                                                {formatCurrency(
                                                    item?.taxableAmount
                                                )}

                                            </td>


                                            {/* CGST */}

                                            <td className="text-right">

                                                {formatCurrency(
                                                    item?.cgstAmount
                                                )}

                                            </td>


                                            {/* SGST */}

                                            <td className="text-right">

                                                {formatCurrency(
                                                    item?.sgstAmount
                                                )}

                                            </td>


                                            {/* IGST */}

                                            <td className="text-right">

                                                {formatCurrency(
                                                    item?.igstAmount
                                                )}

                                            </td>


                                            {/* CESS */}

                                            <td className="text-right">

                                                {formatCurrency(
                                                    item?.cessAmount
                                                )}

                                            </td>


                                            {/* INPUT TAX */}

                                            <td className="text-right">

                                                <strong className="gst-purchase-input-tax">

                                                    {formatCurrency(
                                                        item?.taxAmount
                                                    )}

                                                </strong>

                                            </td>


                                            {/* GRAND TOTAL */}

                                            <td className="text-right">

                                                <strong>

                                                    {formatCurrency(
                                                        item?.grandTotal
                                                    )}

                                                </strong>

                                            </td>


                                            {/* RCM */}

                                            <td>

                                                <span
                                                    className={
                                                        item?.reverseCharge
                                                            ? "gst-purchase-rcm gst-purchase-rcm-yes"
                                                            : "gst-purchase-rcm gst-purchase-rcm-no"
                                                    }
                                                >

                                                    {item?.reverseCharge
                                                        ? "YES"
                                                        : "NO"}

                                                </span>

                                            </td>


                                            {/* STATUS */}

                                            <td>

                                                <span
                                                    className={`gst-purchase-status gst-purchase-status-${String(
                                                        item?.documentStatus ||
                                                        ""
                                                    ).toLowerCase()}`}
                                                >

                                                    {item?.documentStatus ||
                                                        "-"}

                                                </span>

                                            </td>

                                        </tr>

                                    )
                                )}

                            </tbody>

                        </table>

                    </div>

                )}


                {/* =================================================
                    PAGINATION
                ================================================= */}

                <div className="gst-purchase-pagination">

                    <div>

                        Showing{" "}

                        <strong>
                            {fromRecord}
                        </strong>

                        {" - "}

                        <strong>
                            {toRecord}
                        </strong>

                        {" of "}

                        <strong>
                            {total}
                        </strong>

                    </div>


                    <div className="gst-purchase-pagination-controls">

                        <select
                            value={
                                limit
                            }
                            onChange={(
                                event
                            ) => {
                                setLimit(
                                    Number(
                                        event.target.value
                                    )
                                );

                                setPage(1);
                            }}
                        >

                            <option value={10}>
                                10 / page
                            </option>

                            <option value={20}>
                                20 / page
                            </option>

                            <option value={50}>
                                50 / page
                            </option>

                            <option value={100}>
                                100 / page
                            </option>

                        </select>


                        <button
                            type="button"
                            disabled={
                                currentPage <= 1
                            }
                            onClick={() =>
                                setPage(
                                    (
                                        current
                                    ) =>
                                        Math.max(
                                            current - 1,
                                            1
                                        )
                                )
                            }
                        >
                            <FiChevronLeft />
                        </button>


                        <span>

                            Page{" "}

                            <strong>
                                {currentPage}
                            </strong>

                            {" of "}

                            <strong>
                                {totalPages}
                            </strong>

                        </span>


                        <button
                            type="button"
                            disabled={
                                currentPage >=
                                totalPages
                            }
                            onClick={() =>
                                setPage(
                                    (
                                        current
                                    ) =>
                                        Math.min(
                                            current + 1,
                                            totalPages
                                        )
                                )
                            }
                        >
                            <FiChevronRight />
                        </button>

                    </div>

                </div>

            </section>

        </div>
    );
};


export default PurchaseGstRegister;